/** Stable tab-key definitions and pure helpers for TranslationRightPane. */

export const FULL_TAB_ORDER = [
    'segments',
    'image-references',
    'draft-compare',
    'dbs-references',
    'signs',
    'passage-key-terms',
    'translation-resource-library',
    'passage-docs',
] as const

export type TabKey = typeof FULL_TAB_ORDER[number]

export const SAFE_FALLBACK_TAB: TabKey = 'segments'

/**
 * Resolve the initial stable tab key from persisted preferences.
 *
 * Priority:
 *   1. TRANSLATIONRIGHTPANETAB – a known stable key wins immediately.
 *   2. TRANSLATIONRIGHTPANEINDEX – legacy numeric, mapped against FULL_TAB_ORDER.
 *   3. SAFE_FALLBACK_TAB.
 */
export function resolveTabKey(
    storedTabKey: string | null,
    legacyIndexStr: string | null,
): TabKey {
    if (storedTabKey && (FULL_TAB_ORDER as readonly string[]).includes(storedTabKey)) {
        return storedTabKey as TabKey
    }
    const idx = parseInt(legacyIndexStr ?? '', 10)
    if (!isNaN(idx) && idx >= 0 && idx < FULL_TAB_ORDER.length) {
        return FULL_TAB_ORDER[idx]
    }
    return SAFE_FALLBACK_TAB
}

/**
 * Return the selectedIndex react-tabs needs given the currently visible ordered tab keys
 * and the user's chosen stable key. Falls back to 0 when the key is not visible.
 */
export function resolvedSelectedIndex(visibleKeys: readonly TabKey[], selectedKey: TabKey): number {
    const idx = visibleKeys.indexOf(selectedKey)
    return idx >= 0 ? idx : 0
}

/** Return the full-order index for a key; used when writing TRANSLATIONRIGHTPANEINDEX. */
export function legacyIndexForKey(key: TabKey): number {
    return FULL_TAB_ORDER.indexOf(key)
}

/**
 * Build the ordered list of currently visible tab keys.
 * This order must stay in sync with the Tab and TabPanel rendering order
 * in TranslationRightPane.
 *
 * showTranslationResourceLibrary must be translationResourceLibrary === true;
 * undefined and false both exclude TRL (fail-closed).
 */
export function buildVisibleTabKeys(options: {
    displayPassageKeyTermsTab: boolean
    showTranslationResourceLibrary: boolean
}): TabKey[] {
    return [
        'segments',
        'image-references',
        'draft-compare',
        'dbs-references',
        'signs',
        ...(options.displayPassageKeyTermsTab ? ['passage-key-terms' as TabKey] : []),
        ...(options.showTranslationResourceLibrary ? ['translation-resource-library' as TabKey] : []),
        'passage-docs',
    ]
}
